import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { Alert, Button, Card, Checkbox, Field, Select, Textarea } from '../../components/ui';
import { t } from '../../i18n';
import { api } from '../../lib/api';
import { useMe } from '../../lib/auth';
import { errorMessage } from '../../lib/errors';
import { usePeople } from '../projects/data';
import { type Priority, PRIORITIES, priorityName } from '../projects/requests-data';
import { buildRule, type RecurrenceForm } from './data';
import { RecurrenceEditor } from './RecurrenceEditor';

/** Tarea personal (guía 7.3): la ven quien la crea y sus asignados. Puede repetirse. */
export function NewTaskPage() {
  const me = useMe();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const people = usePeople(me.organizationId);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<Priority>('media');
  const [startDate, setStartDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignees, setAssignees] = useState<string[]>([me.id]);
  const [recurrence, setRecurrence] = useState<RecurrenceForm>({ freq: 'none', interval: 1, days: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const created = await api<{ id: string }>('/api/tareas', {
        json: {
          title: title.trim(),
          description: description.trim() || null,
          priority,
          startDate: startDate || null,
          dueDate: dueDate || null,
          assigneeIds: assignees,
          recurrenceRule: buildRule(recurrence),
        },
      });
      await qc.invalidateQueries({ queryKey: ['tareas'] });
      void navigate(`/tareas/${created.id}`);
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-4">
      <div>
        <Link to="/tareas" className="text-sm text-marca hover:underline">
          ← {t('ta.titulo')}
        </Link>
        <h1 className="mt-1 text-2xl font-semibold text-tinta">{t('ta.nueva')}</h1>
        <p className="text-sm text-slate-600">{t('ta.nueva_ayuda')}</p>
      </div>
      <Card>
        <form onSubmit={submit} className="space-y-4">
          {error && <Alert>{error}</Alert>}
          <Field id="tn-titulo" label={t('ta.titulo_campo')} required maxLength={300} value={title} onChange={(e) => setTitle(e.target.value)} />
          <Textarea id="tn-descripcion" label={t('ta.descripcion')} rows={3} maxLength={10_000} value={description} onChange={(e) => setDescription(e.target.value)} />
          <div className="grid gap-4 sm:grid-cols-3">
            <Select id="tn-prioridad" label={t('ta.prioridad')} value={priority} onChange={(e) => setPriority(e.target.value as Priority)}>
              {PRIORITIES.map((p) => (
                <option key={p} value={p}>
                  {priorityName(p)}
                </option>
              ))}
            </Select>
            <Field id="tn-inicio" type="date" label={t('ta.inicio')} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <Field id="tn-vence" type="date" label={t('ta.vence')} value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <fieldset className="space-y-1">
            <legend className="text-sm font-medium text-slate-700">{t('ta.asignados')}</legend>
            <div className="max-h-40 space-y-1 overflow-y-auto">
              {(people.data ?? [{ id: me.id, fullName: me.fullName, email: me.email }]).map((p) => (
                <Checkbox key={p.id} label={p.id === me.id ? t('ta.yo', { nombre: p.fullName }) : p.fullName} checked={assignees.includes(p.id)} onChange={(e) => setAssignees(e.target.checked ? [...assignees, p.id] : assignees.filter((x) => x !== p.id))} />
              ))}
            </div>
          </fieldset>
          <RecurrenceEditor value={recurrence} onChange={setRecurrence} idPrefix="tn" />
          {recurrence.freq !== 'none' && !dueDate && <p className="text-xs text-amber-700">{t('ta.repetir_sin_fecha')}</p>}
          <div className="flex gap-2">
            <Button type="submit" disabled={busy || !title.trim()}>
              {t('ta.crear')}
            </Button>
            <Link to="/tareas" className="rounded border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              {t('comun.cancelar')}
            </Link>
          </div>
        </form>
      </Card>
    </div>
  );
}
